/** Сессия расчёта на вкладке «ФБС» — состояние экрана между нажатиями.
 *
 *  Держит ровно три вещи: ЧТО посчитано (`applied` — ключ запроса отчёта), КАКИМ нажатием
 *  (`calcNonce`) и чем кончилось создание документа (`justCreated`). Живёт отдельно от страницы,
 *  потому что оба её правила проверяются тестом (`fbsCalcSession.test.tsx`) без рендера таблицы.
 *
 *  ① «Рассчитать» ВСЕГДА ходит на сервер. Отчёт лежит в кэше с `staleTime: Infinity`, а ключ —
 *     это параметры; без счётчика нажатий повтор с теми же параметрами отдавал бы старые числа,
 *     не сходив ни в DWH, ни в живую 1С, — а жмут кнопку ровно ради свежих.
 *  ② По одному расчёту создаётся РОВНО один документ. После «Заказа на перемещение» расчёт под
 *     ним уже неверен (он не знает про свой же документ в «в пути»), и второй документ по нему —
 *     дубль в 1С. Поэтому `created()` снимает `applied` целиком: нет отчёта — нет и кнопки
 *     «Создать заказ на перемещение», пока не пересчитали.
 */
import { useCallback, useState } from "react";

import type { FbsReportQuery } from "@/api/supply";

/** Чем кончилось создание документа: номер из 1С, либо `null` — «ответ оборвался, документ МОГ
 *  быть создан» (500). Второй случай не молчит: и пересчитать, и проверить 1С надо в обоих. */
export interface FbsCreatedMark {
  number: string | null;
}

export interface FbsCalcSession {
  applied: FbsReportQuery | null;
  calcNonce: number;
  justCreated: FbsCreatedMark | null;
  calc: (query: FbsReportQuery) => void;
  created: (mark: FbsCreatedMark) => void;
}

export function useFbsCalcSession(): FbsCalcSession {
  // Расчёт НЕ стартует сам: пока не нажали «Рассчитать», запроса нет (спрос из DWH + живая 1С).
  const [applied, setApplied] = useState<FbsReportQuery | null>(null);
  const [calcNonce, setCalcNonce] = useState(0);
  const [justCreated, setJustCreated] = useState<FbsCreatedMark | null>(null);

  const calc = useCallback((query: FbsReportQuery): void => {
    setApplied(query);
    setCalcNonce((n) => n + 1);
    setJustCreated(null);
  }, []);

  const created = useCallback((mark: FbsCreatedMark): void => {
    setApplied(null);
    setJustCreated(mark);
  }, []);

  return { applied, calcNonce, justCreated, calc, created };
}
